import { useState, useEffect } from "react";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import b1 from "../assets/b1.jpeg";
import b2 from "../assets/b2.jpeg";
import b3 from "../assets/b3.jpeg";
import b4 from "../assets/b4.jpeg";
import b5 from "../assets/b5.jpeg";
import b6 from "../assets/b6.jpeg";
import { FaFlag, FaShieldAlt, FaTimes } from "react-icons/fa";
import p1 from "../assets/p1.jpeg";
import p2 from "../assets/p2.jpeg";
import p3 from "../assets/p3.jpeg";
import p4 from "../assets/p4.jpeg";
import p5 from "../assets/p5.jpeg";
import p6 from "../assets/p6.jpeg";
import p7 from "../assets/p7.jpeg";
import ph from "../assets/placeholder.jpeg";
// ─── 👇 Drop your video file in /assets and update this import
import heroVideo from "../assets/bannerVideo.mp4";
import Navbar from "./Navbar";

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=DM+Sans:wght@300;400;500&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html, body, #root { width: 100%; }
  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
`;

// ─── Keyframes ────────────────────────────────────────────────────────────────

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const slideTrack = keyframes`
  0%   { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

const modalIn = keyframes`
  from { opacity: 0; transform: translateY(40px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const backdropIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

// ─── Layout ───────────────────────────────────────────────────────────────────

const AppContainer = styled.div`
  position: relative;
  width: 100vw;
  min-height: 100vh;
  overflow: hidden;
  background: #0a0010;
  display: flex;
  justify-content: center;
  align-items: flex-start;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background:
      radial-gradient(
        ellipse 80% 55% at 50% -5%,
        #7404be55 0%,
        transparent 70%
      ),
      radial-gradient(
        ellipse 60% 40% at 20% 100%,
        #a855fd33 0%,
        transparent 65%
      ),
      radial-gradient(ellipse 50% 35% at 80% 90%, #7404be22 0%, transparent 60%);
    z-index: 0;
    pointer-events: none;
  }
`;

const Phone = styled.div`
  position: relative;
  width: 100%;
  min-height: 100vh;
  max-width: ${({ theme }) => (theme.isDesktop ? "1400px" : "430px")};
  margin: 0 auto;
  background: #0a0010;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: ${({ theme }) =>
    theme.isDesktop ? "0 25px 50px -12px rgba(0,0,0,0.25)" : "none"};
  border-radius: ${({ theme }) => (theme.isDesktop ? "48px" : "0")};
  border: ${({ theme }) =>
    theme.isDesktop ? "1px solid rgba(168,85,253,0.2)" : "none"};

  @media (min-width: 768px) {
    max-width: 430px;
  }
  @media (min-width: 1024px) {
    max-width: 3000px;
    min-height: 100vh;
    margin: auto auto;
  }
`;

// ─── Video Hero ───────────────────────────────────────────────────────────────

const VideoHero = styled.div`
  position: relative;
  width: 100%;
  /* full-width, height follows video aspect ratio */
  overflow: hidden;
  flex-shrink: 0;

  /* gradient fade at bottom so it blends into the dark bg */
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      to bottom,
      rgba(10, 0, 16, 0.15) 0%,
      rgba(10, 0, 16, 0.05) 40%,
      rgba(10, 0, 16, 0.55) 80%,
      rgba(10, 0, 16, 1) 100%
    );
    pointer-events: none;
    z-index: 1;
  }
`;

const HeroVideo = styled.video`
  width: 100%;
  height: auto;
  display: block;
  object-fit: cover;
`;

// Overlay content centered on top of the video
const VideoOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => (theme.isDesktop ? "20px" : "14px")};
  padding: ${({ theme }) => (theme.isDesktop ? "0 60px" : "0 28px")};
  animation: ${fadeUp} 1s 0.2s cubic-bezier(0.16, 1, 0.3, 1) both;
`;

const Tagline = styled.p`
  font-size: 5vw;
  font-weight: 100;
  font-style: italic;
  color: rgba(255, 255, 255, 0.85);
  letter-spacing: 0.04em;
  text-align: center;
  line-height: 1;
  text-shadow: 0 3px 10px #333;
  font-family: "Voltage", serif;

  /* @media (min-width: 1024px) {
    font-size: 32px;
  } */
`;

const Cyber = styled.p`
  font-family: "Mendl", serif;
  font-size: ${({ theme }) => (theme.isDesktop ? "14px" : "12px")};
  font-weight: 300;
  font-style: italic;
  color: rgba(197, 191, 191, 0.65);
  letter-spacing: 0.04em;
  text-align: center;

  span {
    text-decoration: underline;
    cursor: pointer;
    transition: color 0.15s ease;
    &:hover {
      color: rgba(168, 85, 253, 0.9);
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => (theme.isDesktop ? "12px" : "10px")};
  align-items: center;
  width: 100%;
  margin-top: ${({ theme }) => (theme.isDesktop ? "4px" : "2px")};
`;

// ─── Carousel ─────────────────────────────────────────────────────────────────

const CarouselOuter = styled.div`
  position: relative;
  z-index: 5;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  padding: ${({ theme }) => (theme.isDesktop ? "20px 0 40px" : "12px 0 32px")};

  &::before,
  &::after {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    width: ${({ theme }) => (theme.isDesktop ? "80px" : "56px")};
    z-index: 20;
    pointer-events: none;
  }
  &::before {
    left: 0;
    background: linear-gradient(to right, #0a0010, transparent);
  }
  &::after {
    right: 0;
    background: linear-gradient(to left, #0a0010, transparent);
  }

  @media (min-width: 1024px) {
    &::before,
    &::after {
      width: 120px;
    }
  }
`;

const CarouselStage = styled.div`
  position: relative;
  width: 100%;
  height: ${({ theme }) => (theme.isDesktop ? "500px" : "360px")};
  min-height: ${({ theme }) => (theme.isDesktop ? "450px" : "280px")};
  display: flex;
  align-items: center;
  @media (min-width: 1024px) {
    height: 550px;
  }
`;

const BgTrack = styled.div`
  position: absolute;
  top: ${({ theme }) => (theme.isDesktop ? "-70px" : "-50px")};
  display: flex;
  gap: ${({ theme }) => (theme.isDesktop ? "20px" : "14px")};
  width: max-content;
  animation: ${slideTrack} 48s linear infinite reverse;
  will-change: transform;
  z-index: 1;
`;

const BgCard = styled.div`
  width: ${({ theme }) => (theme.isDesktop ? "160px" : "120px")};
  height: ${({ theme }) => (theme.isDesktop ? "224px" : "168px")};
  border-radius: ${({ theme }) => (theme.isDesktop ? "24px" : "18px")};
  flex-shrink: 0;
  background: #1a0533;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.5);
  position: relative;
  overflow: visible;
  @media (min-width: 1024px) {
    width: 180px;
    height: 252px;
  }
`;

const BgCardInner = styled.div`
  position: absolute;
  inset: 0;
  border-radius: inherit;
  overflow: hidden;
  opacity: 0.4;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const BgCardOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, transparent 38%, rgba(0, 0, 0, 0.6) 100%);
  z-index: 1;
`;

const BgCardPill = styled.div`
  position: absolute;
  top: ${({ theme }) => (theme.isDesktop ? "12px" : "9px")};
  z-index: 2;
  height: ${({ theme }) => (theme.isDesktop ? "26px" : "22px")};
  border-radius: 100px;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 ${({ theme }) => (theme.isDesktop ? "10px" : "7px")};
  gap: 3px;
  font-family: "Mendl", sans-serif;
  font-size: ${({ theme }) => (theme.isDesktop ? "12px" : "10px")};
  font-weight: 500;
  color: #fff;
  white-space: nowrap;
`;

const BgCardFlag = styled(BgCardPill)`
  left: ${({ theme }) => (theme.isDesktop ? "12px" : "9px")};
  font-size: ${({ theme }) => (theme.isDesktop ? "10px" : "8px")};
  padding: 0 ${({ theme }) => (theme.isDesktop ? "8px" : "6px")};
  background-color: red;
`;

const FlagNotif = styled.div`
  position: absolute;
  top: 4px;
  left: 22px;
  width: ${({ theme }) => (theme.isDesktop ? "18px" : "14px")};
  height: ${({ theme }) => (theme.isDesktop ? "18px" : "14px")};
  border-radius: 50%;
  background: #80413e;
  border: 1.5px solid #795e88;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Mendl", sans-serif;
  font-size: ${({ theme }) => (theme.isDesktop ? "9px" : "7.5px")};
  font-weight: 700;
  color: #fff;
  line-height: 1;
  z-index: 10;
`;

const BgCardCount = styled(BgCardPill)`
  right: ${({ theme }) => (theme.isDesktop ? "12px" : "9px")};
  font-size: ${({ theme }) => (theme.isDesktop ? "11px" : "9.5px")};
`;

const BgCardInfo = styled.div`
  position: absolute;
  bottom: ${({ theme }) => (theme.isDesktop ? "14px" : "10px")};
  left: ${({ theme }) => (theme.isDesktop ? "14px" : "10px")};
  z-index: 2;
`;

const BgCardName = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: ${({ theme }) => (theme.isDesktop ? "14px" : "12px")};
  font-weight: 500;
  color: #fff;
  letter-spacing: 0.02em;
  line-height: 1.2;
`;

const BgCardAge = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: ${({ theme }) => (theme.isDesktop ? "12px" : "10px")};
  font-weight: 300;
  color: rgba(255, 255, 255, 0.7);
`;

const FgTrack = styled.div`
  position: absolute;
  bottom: 0;
  display: flex;
  gap: ${({ theme }) => (theme.isDesktop ? "24px" : "16px")};
  width: max-content;
  animation: ${slideTrack} 56s linear infinite;
  will-change: transform;
  z-index: 3;
  top: ${({ theme }) => (theme.isDesktop ? "70px" : "50px")};
`;

const FgCard = styled.div`
  width: ${({ theme }) => (theme.isDesktop ? "400px" : "295px")};
  height: ${({ theme }) => (theme.isDesktop ? "500px" : "360px")};
  min-width: ${({ theme }) => (theme.isDesktop ? "350px" : "200px")};
  min-height: ${({ theme }) => (theme.isDesktop ? "450px" : "280px")};
  border-radius: ${({ theme }) => (theme.isDesktop ? "32px" : "24px")};
  flex-shrink: 0;
  overflow: hidden;
  position: relative;
  box-shadow:
    0 12px 40px rgba(0, 0, 0, 0.65),
    0 0 0 1px rgba(255, 255, 255, 0.1) inset;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0.05) 0%,
      transparent 30%,
      transparent 65%,
      rgba(0, 0, 0, 0.28) 100%
    );
    pointer-events: none;
  }
  @media (min-width: 1024px) {
    width: 450px;
    height: 550px;
  }
`;

const FgCardTitle = styled.div`
  position: absolute;
  top: 60%;
  left: 50%;
  transform: translateX(-50%);
  z-index: 4;
  width: 82%;
  text-align: center;
  font-family: "Mendl", sans-serif;
  font-size: ${({ theme }) => (theme.isDesktop ? "48px" : "35px")};
  font-weight: 900;
  color: #fff;
  letter-spacing: 0.06em;
  line-height: 1.15;
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.6);
  padding: 5px 0;
  word-wrap: break-word;
  @media (min-width: 1024px) {
    font-size: 56px;
  }
`;

// ─── Modal ────────────────────────────────────────────────────────────────────

const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100;
  background: rgba(5, 0, 14, 0.72);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  display: flex;
  align-items: ${({ theme }) => (theme.isDesktop ? "center" : "flex-end")};
  justify-content: ${({ theme }) =>
    theme.isDesktop ? "center" : "flex-start"};
  animation: ${backdropIn} 0.28s ease both;
`;

const ModalSheet = styled.div`
  width: 100%;
  max-width: ${({ theme }) => (theme.isDesktop ? "600px" : "100%")};
  max-height: ${({ theme }) => (theme.isDesktop ? "80vh" : "85vh")};
  border-radius: ${({ theme }) => (theme.isDesktop ? "32px" : "28px 28px 0 0")};
  background: linear-gradient(160deg, #ffffff 0%, #ffffff 100%);
  border: 1px solid rgba(168, 85, 253, 0.2);
  box-shadow: 0 -8px 48px rgba(116, 4, 190, 0.35);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: ${modalIn} 0.35s cubic-bezier(0.16, 1, 0.3, 1) both;
  margin: ${({ theme }) => (theme.isDesktop ? "20px" : "0")};
`;

const ModalHandle = styled.div`
  width: 36px;
  height: 4px;
  border-radius: 100px;
  background: rgba(0, 0, 0, 0.12);
  margin: 14px auto 0;
  flex-shrink: 0;
  display: ${({ theme }) => (theme.isDesktop ? "none" : "block")};
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => (theme.isDesktop ? "20px 24px" : "16px 22px 12px")};
  flex-shrink: 0;
`;

const ModalTitle = styled.h2`
  font-family: "Mendl", serif;
  font-size: ${({ theme }) => (theme.isDesktop ? "24px" : "22px")};
  font-weight: 400;
  font-style: italic;
  color: #2b2929;
  letter-spacing: 0.04em;
  display: flex;
  align-items: center;
  gap: 10px;
  svg {
    color: #a855fd;
    font-size: ${({ theme }) => (theme.isDesktop ? "22px" : "18px")};
    flex-shrink: 0;
  }
`;

const ModalClose = styled.button`
  width: ${({ theme }) => (theme.isDesktop ? "36px" : "32px")};
  height: ${({ theme }) => (theme.isDesktop ? "36px" : "32px")};
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.12);
  background: rgba(0, 0, 0, 0.05);
  color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: ${({ theme }) => (theme.isDesktop ? "15px" : "13px")};
  flex-shrink: 0;
  transition: all 0.15s ease;
  &:hover {
    background: rgba(0, 0, 0, 0.1);
    transform: scale(1.05);
  }
  &:active {
    transform: scale(0.95);
  }
`;

const ModalScrollBody = styled.div`
  overflow-y: auto;
  padding: ${({ theme }) => (theme.isDesktop ? "0 24px 32px" : "0 22px 36px")};
  flex: 1;
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(168, 85, 253, 0.4);
    border-radius: 100px;
  }
`;

const ModalImage = styled.div`
  width: 100%;
  height: ${({ theme }) => (theme.isDesktop ? "220px" : "190px")};
  border-radius: 18px;
  overflow: hidden;
  margin-bottom: 22px;
  position: relative;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      160deg,
      rgba(116, 4, 190, 0.3) 0%,
      transparent 60%
    );
    border-radius: 18px;
  }
`;

const ModalDivider = styled.div`
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(168, 85, 253, 0.3),
    transparent
  );
  margin: 18px 0;
`;

const ModalSection = styled.div`
  margin-bottom: 18px;
`;

const ModalSectionTitle = styled.h3`
  font-family: "Mendl", serif;
  font-size: ${({ theme }) => (theme.isDesktop ? "12px" : "11px")};
  font-weight: 600;
  font-style: italic;
  color: #a855fd;
  letter-spacing: 0.05em;
  margin-bottom: 8px;
  text-transform: uppercase;
`;

const ModalBody = styled.p`
  font-family: "Mendl Light", sans-serif;
  font-size: ${({ theme }) => (theme.isDesktop ? "14px" : "13.5px")};
  font-weight: 100;
  color: rgba(56, 52, 52, 0.72);
  line-height: 1.75;
  letter-spacing: 0.01em;
`;

// ─── Buttons ──────────────────────────────────────────────────────────────────

const glassBase = `
  position: relative;
  width: 100%;
  max-width: 320px;
  height: 56px;
  border-radius: 100px;
  border: none;
  cursor: pointer;
  overflow: hidden;
  font-family: 'Mendl', sans-serif;
  font-size: 15px;
  font-weight: 800;
  letter-spacing: 0.04em;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.25s cubic-bezier(0.23,1,0.32,1), box-shadow 0.25s cubic-bezier(0.23,1,0.32,1);
  -webkit-tap-highlight-color: transparent;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 12%; right: 12%;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent);
    border-radius: 50%;
    z-index: 2;
    pointer-events: none;
  }
  &:active { transform: scale(0.97); }

  @media (min-width: 1024px) { max-width: 360px; height: 64px; font-size: 16px; }
`;

const LiquidGlassPrimary = styled.button`
  ${glassBase}
  background: #f2356e18;
  backdrop-filter: blur(24px) saturate(180%);
  -webkit-backdrop-filter: blur(24px) saturate(180%);
  box-shadow:
    0 0 0 1px rgba(200, 140, 255, 0.45),
    0 1.5px 0 1px rgba(255, 255, 255, 0.18) inset,
    0 -1px 0 1px rgba(0, 0, 0, 0.25) inset,
    0 8px 32px rgba(116, 4, 190, 0.5),
    0 2px 8px rgba(0, 0, 0, 0.3);
  color: #fff;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  &:hover {
    background: #f2356e;
    transform: translateY(-2px) scale(1.015);
    box-shadow:
      0 0 0 1px rgba(220, 120, 255, 0.7),
      0 1.5px 0 1px rgba(255, 255, 255, 0.22) inset,
      0 -1px 0 1px rgba(0, 0, 0, 0.2) inset,
      0 14px 50px rgba(116, 4, 190, 0.65),
      0 4px 14px rgba(0, 0, 0, 0.35);
  }
`;

const LiquidGlassSecondary = styled.button`
  ${glassBase}
  background: linear-gradient(160deg, rgba(255,255,255,0.18) 0%, rgba(180,180,180,0.10) 40%, rgba(255,255,255,0.06) 100%);
  backdrop-filter: blur(28px) saturate(160%) brightness(1.12);
  -webkit-backdrop-filter: blur(28px) saturate(160%) brightness(1.12);
  box-shadow:
    0 0 0 1px rgba(255, 255, 255, 0.28),
    0 1.5px 0 1px rgba(255, 255, 255, 0.22) inset,
    0 -1px 0 1px rgba(0, 0, 0, 0.18) inset,
    0 6px 28px rgba(0, 0, 0, 0.3),
    0 2px 8px rgba(0, 0, 0, 0.2);
  color: rgba(255, 255, 255, 0.92);
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  &:hover {
    transform: translateY(-2px) scale(1.015);
    background: linear-gradient(
      160deg,
      rgba(255, 255, 255, 0.26) 0%,
      rgba(200, 200, 200, 0.14) 40%,
      rgba(255, 255, 255, 0.1) 100%
    );
    box-shadow:
      0 0 0 1px rgba(255, 255, 255, 0.42),
      0 1.5px 0 1px rgba(255, 255, 255, 0.28) inset,
      0 -1px 0 1px rgba(0, 0, 0, 0.15) inset,
      0 10px 36px rgba(0, 0, 0, 0.36),
      0 3px 12px rgba(0, 0, 0, 0.25);
  }
`;

// ─── Data ─────────────────────────────────────────────────────────────────────

const PHOTO_IMAGES = [
  { src: b1, title: "Feel the Spark" },
  { src: b4, title: "Real People" },
  { src: b2, title: "Raw Connection" },
  { src: b6, title: "Security First" },
  { src: b5, title: "Discover More" },
  { src: b3, title: "Explore Boldly" },
];

const PROFILE_CARDS = [
  { name: "Amanda", age: 21, flag: "🇮🇹", count: 0, notif: null, img: p4 },
  { name: "Lucas", age: 29, flag: "🇧🇷", count: 12, notif: null, img: p1 },
  { name: "Mia", age: 24, flag: "🇫🇷", count: 9, notif: 1, img: p2 },
  { name: "Richard", age: 31, flag: "🇺🇸", count: 9, notif: 2, img: p3 },
  { name: "Aisha", age: 27, flag: "🇳🇬", count: 5, notif: null, img: p5 },
  { name: "Elena", age: 25, flag: "🇬🇷", count: 2, notif: null, img: p7 },
  { name: "Noah", age: 28, flag: "🇬🇧", count: 14, notif: 2, img: p6 },
];

// ─── Carousel ─────────────────────────────────────────────────────────────────

function LayeredCarousel({ isDesktop }) {
  const bgCards = [...PROFILE_CARDS, ...PROFILE_CARDS, ...PROFILE_CARDS];
  const fgImages = [...PHOTO_IMAGES, ...PHOTO_IMAGES, ...PHOTO_IMAGES];

  return (
    <CarouselStage theme={{ isDesktop }}>
      <BgTrack theme={{ isDesktop }}>
        {bgCards.map((c, i) => (
          <BgCard key={i} theme={{ isDesktop }}>
            <BgCardInner>
              <img src={c.img} alt="" draggable="false" />
              <BgCardOverlay />
              <BgCardFlag theme={{ isDesktop }}>
                <FaFlag />
              </BgCardFlag>
              <BgCardCount theme={{ isDesktop }}>
                {c.count === 0 ? "V" : c.count}
              </BgCardCount>
              <BgCardInfo theme={{ isDesktop }}>
                <BgCardName theme={{ isDesktop }}>{c.name}</BgCardName>
                <BgCardAge theme={{ isDesktop }}>{c.age} years old</BgCardAge>
              </BgCardInfo>
            </BgCardInner>
            {c.notif && <FlagNotif theme={{ isDesktop }}>{c.notif}</FlagNotif>}
          </BgCard>
        ))}
      </BgTrack>

      <FgTrack theme={{ isDesktop }}>
        {fgImages.map((item, i) => (
          <FgCard key={i} theme={{ isDesktop }}>
            <img src={item.src} alt="" draggable="false" />
            <FgCardTitle theme={{ isDesktop }}>{item.title}</FgCardTitle>
          </FgCard>
        ))}
      </FgTrack>
    </CarouselStage>
  );
}

// ─── App ──────────────────────────────────────────────────────────────────────

export default function App() {
  const [showModal, setShowModal] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const theme = { isDesktop };

  return (
    <>
      <GlobalStyle />
      <Navbar />
      <AppContainer>
        <Phone theme={theme}>
          {/* ── Video Hero with overlaid title + buttons ── */}
          <VideoHero>
            <HeroVideo src={heroVideo} autoPlay muted loop playsInline />
            <VideoOverlay theme={theme}>
              <Tagline theme={theme}>
                Raw Attraction. <br /> Real People.
              </Tagline>
              <Cyber theme={theme}>
                Learn more about our{" "}
                <span onClick={() => setShowModal(true)}>Cybersecurity</span>
              </Cyber>
              <ButtonGroup theme={theme}>
                <LiquidGlassPrimary>Create Account</LiquidGlassPrimary>
                <LiquidGlassSecondary>Sign In</LiquidGlassSecondary>
              </ButtonGroup>
            </VideoOverlay>
          </VideoHero>

          {/* ── Carousels below video ── */}
          <CarouselOuter theme={theme}>
            <LayeredCarousel isDesktop={isDesktop} />
          </CarouselOuter>
        </Phone>
      </AppContainer>

      {/* ── Cybersecurity Modal ── */}
      {showModal && (
        <ModalBackdrop theme={theme} onClick={() => setShowModal(false)}>
          <ModalSheet theme={theme} onClick={(e) => e.stopPropagation()}>
            <ModalHandle theme={theme} />
            <ModalHeader theme={theme}>
              <ModalTitle theme={theme}>
                <FaShieldAlt />
                Your Security
              </ModalTitle>
              <ModalClose theme={theme} onClick={() => setShowModal(false)}>
                <FaTimes />
              </ModalClose>
            </ModalHeader>
            <ModalScrollBody theme={theme}>
              <ModalImage theme={theme}>
                <img src={ph} alt="Security" draggable="false" />
              </ModalImage>

              {[
                {
                  title: "Our Commitment",
                  body: "Your safety is the foundation everything here is built on. We designed our security infrastructure from the ground up with one goal: to make sure every interaction you have on this platform is private, protected, and completely in your control.",
                },
                {
                  title: "End-to-End Encryption",
                  body: "Every message you send is encrypted end-to-end using AES-256 — the same standard used by financial institutions and governments worldwide. Not even our own servers can read your conversations. Your words belong only to you and the person you're speaking with.",
                },
                {
                  title: "Identity Verification",
                  body: "Every profile goes through a real-time identity verification process before being activated. We use document scanning combined with liveness detection to ensure the person behind the screen is exactly who they say they are. No bots. No fake profiles. No exceptions.",
                },
                {
                  title: "Data Privacy",
                  body: "We never sell your data — to anyone, ever. Your personal information, location, and activity patterns are never shared with third-party advertisers. You can request a full export or permanent deletion of your data at any time, and we will process it within 48 hours, no questions asked.",
                },
                {
                  title: "Photo & Media Protection",
                  body: "All photos and media you share are stored in encrypted containers and are never indexed or accessible outside of the app. Screenshots within private chats are detected and the other party is notified immediately. Your images cannot be downloaded or saved without your explicit permission.",
                },
                {
                  title: "Location Safety",
                  body: "We never expose your precise GPS coordinates. When location is enabled, we show only a generalised proximity — enough to help you find people nearby, never enough for anyone to track or locate you. You can disable location entirely at any time from your settings.",
                },
                {
                  title: "Reporting & Moderation",
                  body: "Our moderation team operates 24/7. Any profile or message can be reported with a single tap, and our team reviews every report within minutes. Repeated violations result in permanent bans and, where applicable, reports to the relevant authorities. We take abuse seriously — always.",
                },
              ].map((s, i, arr) => (
                <div key={i}>
                  <ModalSection>
                    <ModalSectionTitle theme={theme}>
                      {s.title}
                    </ModalSectionTitle>
                    <ModalBody theme={theme}>{s.body}</ModalBody>
                  </ModalSection>
                  {i < arr.length - 1 && <ModalDivider />}
                </div>
              ))}
            </ModalScrollBody>
          </ModalSheet>
        </ModalBackdrop>
      )}
    </>
  );
}
